import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import { T, M } from 'TOMA';
import s from './Crm.module.scss';
import routes from 'router/routes';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { iCrmCompanyForm } from 'services/Crm/Crm.type';

function CrmCompanyFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const isEdit = !!companyId;
  const { crmRepository } = useApiContext();
  const { t } = useTranslation();
  const [siretLoading, setSiretLoading] = useState(false);
  const [siretInput, setSiretInput] = useState('');

  const { response: existing, loading } = useRepo(
    async () =>
      isEdit ? await crmRepository.getCompany(Number(companyId)) : null,
    [companyId]
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<iCrmCompanyForm>();

  useEffect(() => {
    if (existing) {
      reset({
        name: existing.name,
        siret: existing.siret ?? '',
        siren: existing.siren ?? '',
        address: existing.address ?? '',
        city: existing.city ?? '',
        postal_code: existing.postal_code ?? '',
        country: existing.country ?? '',
        phone: existing.phone ?? '',
        email: existing.email ?? '',
        website: existing.website ?? '',
        notes: existing.notes ?? '',
      });
    }
  }, [existing, reset]);

  const onSubmit = async (data: iCrmCompanyForm): Promise<void> => {
    try {
      if (isEdit) {
        await crmRepository.updateCompany(Number(companyId), data);
        toast.success(t('crm:company_updated'));
      } else {
        await crmRepository.createCompany(data);
        toast.success(t('crm:company_created'));
      }
      navigate(routes.crmCompanies.path);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleSiretLookup = async (): Promise<void> => {
    if (!siretInput) return;
    setSiretLoading(true);
    try {
      const result = await crmRepository.siretLookup(siretInput);
      setValue('name', result.name);
      setValue('siret', result.siret);
      setValue('siren', result.siren);
      setValue('address', result.address);
      setValue('city', result.city);
      setValue('postal_code', result.postal_code);
      setValue('country', result.country);
    } catch {
      toast.error(t('crm:siret_not_found'));
    } finally {
      setSiretLoading(false);
    }
  };

  if (isEdit && loading) {
    return (
      <T.Container>
        <M.Loading loading={true} error={null} fullWiewport>
          <div />
        </M.Loading>
      </T.Container>
    );
  }

  return (
    <div>
      <T.Container>
        <div className={s.pageHeader}>
          <h1 className={s.pageTitle}>
            {isEdit ? t('crm:edit_company') : t('crm:new_company')}
          </h1>
        </div>

        {!isEdit && (
          <div className={s.siretCard}>
            <p className={s.siretTitle}>{t('crm:siret_lookup')}</p>
            <div className={s.siretRow}>
              <input
                type="text"
                value={siretInput}
                onChange={(e) => setSiretInput(e.target.value)}
                placeholder={t('crm:siret_lookup_placeholder')}
                className={s.formInput}
                style={{ maxWidth: 320 }}
              />
              <button
                className={s.btnSecondary}
                onClick={handleSiretLookup}
                disabled={siretLoading}
              >
                {siretLoading ? '...' : t('crm:siret_lookup_btn')}
              </button>
            </div>
          </div>
        )}

        <div className={s.formCard}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={s.formGrid}>
              <div className={s.formGroup}>
                <label className={s.formLabel}>
                  {t('crm:company_name')}
                  <span className={s.required}> *</span>
                </label>
                <input
                  type="text"
                  {...register('name', { required: true })}
                  className={`${s.formInput} ${
                    errors.name ? s.inputError : ''
                  }`}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:siret')}</label>
                <input
                  type="text"
                  {...register('siret')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:siren')}</label>
                <input
                  type="text"
                  {...register('siren')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:address')}</label>
                <input
                  type="text"
                  {...register('address')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:city')}</label>
                <input
                  type="text"
                  {...register('city')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:postal_code')}</label>
                <input
                  type="text"
                  {...register('postal_code')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:country')}</label>
                <input
                  type="text"
                  {...register('country')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:phone')}</label>
                <input
                  type="text"
                  {...register('phone')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:email')}</label>
                <input
                  type="email"
                  {...register('email')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroup}>
                <label className={s.formLabel}>{t('crm:website')}</label>
                <input
                  type="text"
                  {...register('website')}
                  className={s.formInput}
                />
              </div>
              <div className={s.formGroupFull}>
                <label className={s.formLabel}>{t('crm:notes')}</label>
                <textarea
                  {...register('notes')}
                  rows={4}
                  className={s.formTextarea}
                />
              </div>
            </div>

            <div className={s.formActions}>
              <button
                type="submit"
                className={s.btnPrimary}
                disabled={isSubmitting}
              >
                {isSubmitting ? '...' : t('crm:save')}
              </button>
              <button
                type="button"
                className={s.btnLink}
                onClick={() => navigate(routes.crmCompanies.path)}
              >
                {t('crm:cancel')}
              </button>
            </div>
          </form>
        </div>
      </T.Container>
    </div>
  );
}

export default CrmCompanyFormPage;
